import { FC, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import {
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Paper,
  Typography,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  Select,
  TablePagination,
} from '@material-ui/core';
// import ControlPointIcon from '@material-ui/icons/ControlPoint';
import PreviewIcon from '@material-ui/icons/RemoveRedEyeSharp';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import { orange, red } from '@material-ui/core/colors';
import axios from 'server';
import { SamlFormData, SamlServiceProvider, ProviderData, defaultValues } from './types';
import IconButton from '@material-ui/core/IconButton';
import googleguidlineImage from '../../../assets/googlesamlguide.png';
import microsoftguidlineImage from '../../../assets/microsoftsamlguide.png';
import githubguidlineImage from '../../../assets/githubsamlguide.png';
import zoomguidlineImage from '../../../assets/zoomsamlguide.png';
import { GetApp, Close, FileCopy } from '@material-ui/icons';
import Tooltip from '@material-ui/core/Tooltip';
import { Switcher } from 'components/Switcher';
import { FederationFormData, FederationData, defaultValue } from './types';
import { useAppPreloader } from 'hooks/useAppPreloader';
import { useStore } from 'hooks/useRootStore';
const OTHER = 'Other';
const ServiceTable: FC = () => {
  const {
    notificationStore: { openNotification },
  } = useStore();
  const [open, setOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedSaml, setSelectedSaml] = useState<SamlServiceProvider | null>(null);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [attrMappings, setAttrMappings] = useState<{ [name: string]: string }>({});
  const [editAttrMappings, setEditAttrMappings] = useState<{ [name: string]: string }>({});
  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm<SamlFormData>({ defaultValues });
  const {
    register: registerEdit,
    handleSubmit: handleSubmitEdit,
    reset: resetEdit,
    control: controlEdit,
    setValue: setValueEdit,
    formState: { errors: errorsEdit },
    watch: watchEdit,
  } = useForm<SamlFormData>();
  const [imageZoomOpen, setImageZoomOpen] = useState(false);
  const handleImageZoomOpen = () => {
    setImageZoomOpen(true);
  };
  const handleImageZoomClose = () => {
    setImageZoomOpen(false);
  };
  const handleCertificateDownload = () => {
    const url = selectedSaml?.certificate_download_url;
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  const handlePresetChange = (name: string, isEdit = false) => {
    const preset = serviceProviderPresets.find((p) => p.name === name || p.app_name === name);
    if (!preset) return;
    const setVal = isEdit ? setValueEdit : setValue;
    setVal('entity_id', preset.entity_id);
    setVal('acs_url', preset.acs_url);
    setVal('name_id_format', preset.name_id_format);
    setVal('name_id_attribute', preset.name_id_attribute);
    if (!isEdit) {
      const mappings: { [name: string]: string } = {};
      (preset.attributes || []).forEach((attr) => {
        if (attr.name && attr.source) mappings[attr.name] = attr.source;
      });
      setAttrMappings(mappings);
    }
  };
  const { data: providersResponse } = useQuery<ProviderData[]>('saml/service-providers/providers', async () => {
    const response = await axios.get('saml/service-providers/providers');
    const raw = response?.data?.data ?? response?.data ?? [];
    return Array.isArray(raw) ? raw : [];
  });
  const serviceProviderPresets = [...(providersResponse ?? [])];
  const {
    data: apiResponse,
    isLoading: isFetchingSamlSP,
    isError: isSamlSPError,
    refetch,
  } = useQuery(
    'saml-service-providers',
    async () => {
      const response = await axios.get('saml/service-providers');
      return response;
    },
    { refetchOnWindowFocus: false }
  );
  const getSamlList = (): SamlServiceProvider[] => {
    if (Array.isArray(apiResponse)) return apiResponse;
    if (Array.isArray(apiResponse?.data)) return apiResponse.data;
    if (Array.isArray(apiResponse?.data?.data)) return apiResponse.data.data;
    if (Array.isArray(apiResponse?.data?.items)) return apiResponse.data.items;
    return [];
  };

  const getProviderImage = (name?: string) => {
    const provider = (name || '').toLowerCase();
    if (provider.includes('google')) {
      return googleguidlineImage;
    }
    if (provider.includes('zoom')) {
      return zoomguidlineImage;
    }
    if (provider.includes('github')) {
      return githubguidlineImage;
    }
    if (provider.includes('microsoft')) {
      return microsoftguidlineImage;
    }
    return googleguidlineImage;
  };
  const samlServiceProviders = getSamlList();
  const createSamlSP = useMutation((data: SamlFormData) => axios.post('saml/service-providers', data), {
    onSuccess: () => {
      openNotification('success', ' create successfully');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || error?.response?.data?.error || 'Something went wrong';
      openNotification('error', message);
    },
  });
  const updateSamlSP = useMutation(
    ({ uuid, data }: { uuid: string; data: SamlFormData }) => axios.put(`saml/service-providers/${uuid}`, data),
    {
      onSuccess: () => {
        openNotification('success', ' Update successfully');
      },

      onError: (error: any) => {
        const message = error?.response?.data?.message || error?.response?.data?.error || 'Failed to parse metadata';

        openNotification('error', message);
      },
    }
  );
  const deleteSamlSP = useMutation((uuid: string) => axios.delete(`saml/service-providers/${uuid}`), {
    onSuccess: () => {
      openNotification('success', ' Remove successfully');
    },

    onError: (error: any) => {
      const message = error?.response?.data?.message || error?.response?.data?.error || 'Failed to parse metadata';

      openNotification('error', message);
    },
  });

  const toggleSamlSP = useMutation(
    ({ uuid, is_active }: { uuid: string; is_active: boolean }) =>
      axios.post(`saml/service-providers/${uuid}/${is_active ? 'disable' : 'enable'}`),
    {
      onSuccess: () => {
        openNotification('success', 'Saml configuration updated successfully');
      },
      onError: (error: any) => {
        const message = error?.response?.data?.message || error?.response?.data?.error || 'Something went wrong';

        openNotification('error', message);
      },
    }
  );

  const toggleFederationSP = useMutation(
    ({ id, is_active }: { id: number; is_active: boolean }) =>
      axios.post(`app-federation/configure/${id}/${is_active ? 'disable' : 'enable'}`),
    {
      onSuccess: () => {
        openNotification('success', 'Federation configuration updated successfully');
      },
      onError: (error: any) => {
        const message = error?.response?.data?.message || error?.response?.data?.error || 'Something went wrong';

        openNotification('error', message);
      },
    }
  );

  const buildAttributeMappingPayload = () => {
    return Object.entries(attrMappings)
      .filter(([name, source]) => source && name)
      .map(([name, source]) => ({ source, name }));
  };

  const onSubmit = (data: SamlFormData) => {
    const { attributes, customAppName, ...rest } = data;
    const payload = {
      ...rest,
      name: data.name === OTHER ? customAppName || '' : data.name,
      attribute_mapping: buildAttributeMappingPayload(),
    };
    createSamlSP.mutate(payload as any, {
      onSuccess: (response) => {
        const responseData = response?.data?.data || response?.data;
        if (responseData) {
          setSelectedSaml(responseData);
          setOpen(false);
          setViewOpen(true);
        }
        reset(defaultValues);
        refetch();
      },
    });
  };
  const handleClose = () => {
    setOpen(false);
    reset(defaultValues);
    resetFederation(defaultValue);
    createSamlSP.reset();
    createFederation.reset();
    setAttrMappings({});
    setMetadataPreview(null);
    setMetadataUrl('');
    autofillMetadata.reset();
  };

  const handleView = (saml: SamlServiceProvider) => {
    if (saml.name?.toLowerCase().includes('microsoft')) {
      const fed = federationList.find((f) => f.service_provider_uuid === saml.uuid || f.name === saml.name);
      if (fed) {
        setSelectedFederation(fed);
        setSelectedSaml(null);
        setViewOpenFederation(true);
        return;
      }
    }
    setSelectedSaml(saml);
    setSelectedFederation(null);
    setViewOpen(true);
  };

  const handleViewClose = () => {
    setViewOpen(false);
    setSelectedSaml(null);
    setSelectedFederation(null);
  };

  const handleEdit = (saml: SamlServiceProvider) => {
    if (saml.name?.toLowerCase().includes('microsoft')) {
      const fed = federationList.find((f) => f.service_provider_uuid === saml.uuid || f.name === saml.name);
      if (fed) {
        handleFederationEdit(fed);
        return;
      }
    }
    setSelectedSaml(saml);
    const isPreset = serviceProviderPresets.some((p) => p.name === saml.name);
    if (isPreset) {
      resetEdit({
        name: saml.name,
        entity_id: saml.entity_id,
        acs_url: saml.acs_url,
        name_id_format: saml.name_id_format,
        name_id_attribute: saml.name_id_attribute,
        sign_assertion: saml.sign_assertion,
        is_active: saml.is_active,
        customAppName: '',
      });
    } else {
      resetEdit({
        name: OTHER,
        customAppName: saml.name,
        entity_id: saml.entity_id,
        acs_url: saml.acs_url,
        name_id_format: saml.name_id_format,
        name_id_attribute: saml.name_id_attribute,
        sign_assertion: saml.sign_assertion,
        is_active: saml.is_active,
      });
    }
    // Pre-populate editAttrMappings from existing attribute_mapping
    const mappings: { [name: string]: string } = {};
    (saml.attribute_mapping || []).forEach((m) => {
      if (m.name && m.source) mappings[m.name] = m.source;
    });
    setEditAttrMappings(mappings);
    setEditOpen(true);
  };

  const handleEditClose = () => {
    setEditOpen(false);
    setSelectedSaml(null);
    setEditAttrMappings({});
    updateSamlSP.reset();
  };
  const onEditSubmit = (data: SamlFormData) => {
    if (!selectedSaml) {
      return;
    }
    const { customAppName, attributes, ...rest } = data;
    const editAttrPayload = Object.entries(editAttrMappings).map(([name, source]) => ({ name, source }));
    const payload = {
      ...rest,
      name: data.name === OTHER ? customAppName || '' : data.name,
      attribute_mapping: editAttrPayload,
    };
    updateSamlSP.mutate(
      { uuid: selectedSaml.uuid, data: payload as any },
      {
        onSuccess: (response) => {
          const updatedData = response?.data?.data || response?.data;
          setEditOpen(false);
          if (updatedData) {
            setSelectedSaml(updatedData);
          }
          refetch();
        },
      }
    );
  };

  const handleDeleteOpen = (saml: SamlServiceProvider) => {
    setSelectedSaml(saml);
    setDeleteOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteOpen(false);
    setSelectedSaml(null);
    deleteSamlSP.reset();
  };

  const handleDeleteConfirm = () => {
    if (!selectedSaml) return;
    deleteSamlSP.mutate(selectedSaml.uuid, {
      onSuccess: () => {
        setDeleteOpen(false);
        setSelectedSaml(null);
        refetch();
      },
    });
  };
  const selectedEditName = watchEdit('name');
  const selectedAppName = watch('name');
  const [viewOpenFederation, setViewOpenFederation] = useState(false);
  const [editOpenFederation, setEditOpenFederation] = useState(false);
  const [deleteOpenFederation, setDeleteOpenFederation] = useState(false);
  const [selectedFederation, setSelectedFederation] = useState<FederationData | null>(null);
  const {
    register: registerFederation,
    handleSubmit: handleSubmitFederation,
    reset: resetFederation,
    setValue: setFederationValue,
    formState: { errors: federationErrors },
  } = useForm<FederationFormData>({
    defaultValues: defaultValue,
  });

  const {
    register: registerFederationEdit,
    handleSubmit: handleSubmitFederationEdit,
    reset: resetFederationEdit,
    control: controlFederationEdit,
    setValue: setFederationValueEdit,
    formState: { errors: federationErrorsEdit },
  } = useForm<FederationFormData>();
  const federationPresets = (providersResponse ?? []).filter((p) => p.type === 'Federation');

  const handleFederationPresetChange = (appName: string, isEdit = false) => {
    const preset = federationPresets.find((p) => p.name === appName);
    if (!preset) return;
    const setVal = isEdit ? setFederationValueEdit : setFederationValue;
    setVal('domain', preset.domain);
    setVal('base_url', preset.base_url);
  };

  const {
    data: federationList = [],
    isLoading: isFetchingFederation,
    isError: isFederationError,
    refetch: refetchFederation,
  } = useQuery<FederationData[]>(
    'app-federation-configure , page, perPage, ',
    async () => {
      const response = await axios.get('app-federation/configure');
      if (Array.isArray(response.data)) {
        return response.data;
      }
      if (Array.isArray(response.data?.data)) {
        return response.data.data;
      }

      if (Array.isArray(response.data?.items)) {
        return response.data.items;
      }

      return [];
    },
    {
      refetchOnWindowFocus: false,
    }
  );

  const createFederation = useMutation((data: FederationFormData) => axios.post('app-federation/configure', data), {
    onSuccess: () => {
      openNotification('success', 'Federation created successfully');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || error?.response?.data?.error || 'Something went wrong';
      openNotification('error', message);
    },
  });

  const updateFederation = useMutation(
    ({ id, data }: { id: number; data: FederationFormData }) => axios.put(`app-federation/configure/${id}`, data),
    {
      onSuccess: () => {
        openNotification('success', 'Federation updated successfully');
      },
      onError: (error: any) => {
        const message = error?.response?.data?.message || error?.response?.data?.error || 'Something went wrong';
        openNotification('error', message);
      },
    }
  );

  const deleteFederation = useMutation((id: number) => axios.delete(`app-federation/configure/${id}`), {
    onSuccess: () => {
      openNotification('success', 'Federation Remove successfully');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || error?.response?.data?.error || 'Something went wrong';
      openNotification('error', message);
    },
  });

  const onSubmitFederation = (data: FederationFormData) => {
    // const { attributes} = data;

    createFederation.mutate(
      { ...data, name: 'Microsoft 365' },
      {
        onSuccess: (response) => {
          const responseData = response?.data?.data || response?.data;
          if (responseData) {
            setSelectedFederation(responseData);
            setOpen(false);
            setViewOpenFederation(true);
          }
          resetFederation(defaultValue);
          refetchFederation();
        },
      }
    );
  };

  const handleFederationEdit = (federation: FederationData) => {
    setSelectedFederation(federation);
    resetFederationEdit({
      name: federation.name,
      tenant_id: federation.tenant_id || '',
      domain: federation.domain || '',
      azure_client_id: federation.azure_client_id || '',
      azure_client_secret: '',
      base_url: '',
      entity_id: '',
    });
    resetEdit({ name: 'Microsoft 365' });
    setEditOpenFederation(true);
  };
  const handleFederationEditClose = () => {
    setEditOpenFederation(false);
    setSelectedFederation(null);
    updateFederation.reset();
  };
  const onFederationEditSubmit = (data: FederationFormData) => {
    if (!selectedFederation) return;
    updateFederation.mutate(
      { id: selectedFederation.id, data },
      {
        onSuccess: () => {
          setEditOpenFederation(false);
          setSelectedFederation(null);
          refetchFederation();
        },
      }
    );
  };

  const handleFederationDeleteOpen = (federation: FederationData) => {
    setSelectedFederation(federation);
    setDeleteOpenFederation(true);
  };
  const handleFederationDeleteClose = () => {
    setDeleteOpenFederation(false);
    setSelectedFederation(null);
    deleteFederation.reset();
  };
  const handleFederationDeleteConfirm = () => {
    if (!selectedFederation) return;
    deleteFederation.mutate(selectedFederation.id, {
      onSuccess: () => {
        setDeleteOpenFederation(false);
        setSelectedFederation(null);
        refetchFederation();
      },
    });
  };

  const handleFederationView = (federation: FederationData) => {
    setSelectedFederation(federation);
    setViewOpenFederation(true);
  };

  const handleFederationViewClose = () => {
    setViewOpenFederation(false);
    setSelectedFederation(null);
  };
  useAppPreloader(
    isFetchingFederation ||
      isFetchingSamlSP ||
      createFederation.isLoading ||
      updateFederation.isLoading ||
      createSamlSP.isLoading ||
      updateSamlSP.isLoading ||
      toggleFederationSP.isLoading ||
      toggleSamlSP.isLoading ||
      deleteFederation.isLoading ||
      deleteSamlSP.isLoading
  );

  const preset = serviceProviderPresets.find((p) => p.name === selectedAppName);
  const attrs = preset?.attributes || [];
  const combinedData: (SamlServiceProvider | FederationData)[] = [...samlServiceProviders, ...federationList];
  const paginatedData = combinedData.slice(page * perPage, page * perPage + perPage);
  const [manual, setManual] = useState(false);
  const [meta, setMeta] = useState(false);
  const [url, setUrl] = useState(false);
  const [metadataPreview, setMetadataPreview] = useState<any>(null);
  const [metadataUrl, setMetadataUrl] = useState('');
  const autofillMetadata = useMutation(
    (payload: FormData | { url: string }) => {
      if (payload instanceof FormData) {
        return axios.post('saml/service-providers/metadata/autofill', payload, {
          headers: {
            'Content-Type': undefined,
          },
        });
      }

      return axios.post('saml/service-providers/metadata/autofill', payload);
    },
    {
      onSuccess: (response) => {
        const data = response?.data?.data || response?.data;
        if (data) {
          setMetadataPreview(data);
          setValue('name', data.name || '');
          setValue('entity_id', data.entity_id || '');
          setValue('acs_url', data.acs_url || '');
          setValue('name_id_format', data.name_id_format || '');
          setValue('name_id_attribute', data.name_id_attribute || '');
        }
      },

      onError: (error: any) => {
        const message = error?.response?.data?.message || error?.response?.data?.error || 'Failed to parse metadata';

        openNotification('error', message);
      },
    }
  );
  return (
    <>
      <Grid container justify='space-between' style={{ marginBottom: 16 }}>
        <Typography variant='body2' style={{ color: '#718096', marginBottom: 14 }}>
          SPs added directly or from a Federations appear here.
        </Typography>
        <Button
          variant='contained'
          color='secondary'
          style={{ backgroundColor: '#5081FF' }}
          onClick={() => setOpen(true)}
        >
          Add SAML Application
        </Button>
      </Grid>
      {isFetchingSamlSP ? (
        <></>
      ) : isSamlSPError || isFederationError ? (
        <Typography color='error' style={{ padding: 20 }}>
          Failed to load SAML Service Providers.
        </Typography>
      ) : samlServiceProviders.length === 0 ? (
        <Typography
          style={{
            padding: 20,
            textAlign: 'center',
          }}
        >
          No SAML Service Providers found.
        </Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>Application</strong>
                </TableCell>

                <TableCell>
                  <strong>Source</strong>
                </TableCell>
                <TableCell>
                  <strong>Status</strong>
                </TableCell>
                <TableCell align='right'>
                  <strong>Actions</strong>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedData.map((combine) => (
                <TableRow key={combine.id} hover>
                  {/* Provider */}
                  <TableCell>
                    <div
                      style={{
                        gap: 12,
                        minWidth: 0,
                      }}
                    >
                      {/* Provider Name */}
                      <Typography
                        style={{
                          fontWeight: 700,
                          fontSize: 15,
                          color: '#111827',
                          wordBreak: 'break-word',
                        }}
                      >
                        {combine.name || '-'}
                      </Typography>
                      <Typography
                        style={{
                          color: '#6b7280',
                          fontSize: 13,
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {combine.entity_id || ('domain' in combine ? combine.domain : '-') || '-'}
                      </Typography>
                    </div>
                  </TableCell>

                  <TableCell>
                    <div>
                      <Typography
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: '#374151',
                          wordBreak: 'break-word',
                        }}
                      >
                        {combine.source || 'Manual entry'}
                      </Typography>

                      {/* {combine.source && (
                        <Typography
                          style={{
                            fontSize: 11,
                            color: '#9ca3af',
                            marginTop: 3,
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere',
                          }}
                        >
                          {combine.source === 'federation'
                            ? 'Federation'
                            : combine.source === 'metadata_url'
                              ? 'Metadata URL'
                              : combine.source === 'upload'
                                ? 'Uploaded metadata'
                                : 'Manual entry'}
                        </Typography>
                      )} */}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      <Chip
                        label={combine?.is_active ? 'Enabled' : 'Disabled'}
                        color='default'
                        style={{
                          fontWeight: 600,
                          color: combine?.is_active ? '#2E7D32' : '#8d8c8c',
                          backgroundColor: combine?.is_active ? '#E8F5E9' : '#dedddd',
                          border: '1px solid black',
                          borderColor: combine?.is_active ? '#4caf50' : '#ccc',
                        }}
                      />
                    </div>
                  </TableCell>
                  {/* <TableCell>
                    <Chip
                      label={combine?.is_active ? 'Enabled' : 'Disabled'}
                      color='default'
                      style={{
                        fontWeight: 600,
                        color: combine?.is_active ? '#2E7D32' : '#8d8c8c',
                        backgroundColor: combine?.is_active ? '#E8F5E9' : '#dedddd',
                        border: '1px solid black',
                        borderColor: combine?.is_active ? '#4caf50' : '#ccc',
                      }}
                    />

                  </TableCell> */}
                  <TableCell align='right'>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        gap: 4,
                      }}
                    >
                      <Tooltip title='Enable/Disable'>
                        <Switcher
                          checked={combine.is_active === true}
                          onChange={() => {
                            const isMicrosoft = combine.name?.toLowerCase().includes('microsoft');

                            if (isMicrosoft && (combine as any).federation_id !== undefined) {
                              toggleFederationSP.mutate(
                                {
                                  id: combine.id,
                                  is_active: combine.is_active,
                                },
                                {
                                  onSuccess: () => refetch(),
                                }
                              );
                            } else if ('uuid' in combine) {
                              toggleSamlSP.mutate(
                                {
                                  uuid: combine.uuid,
                                  is_active: combine.is_active,
                                },
                                {
                                  onSuccess: () => refetch(),
                                }
                              );
                            }
                          }}
                        />
                      </Tooltip>
                      <Tooltip title='View'>
                        <IconButton
                          size='small'
                          onClick={() => {
                            const isMicrosoft = combine.name?.toLowerCase().includes('microsoft');

                            if (isMicrosoft && !('uuid' in combine)) {
                              handleFederationView(combine as FederationData);
                            } else if ('uuid' in combine) {
                              handleView(combine as SamlServiceProvider);
                            }
                          }}
                        >
                          <PreviewIcon style={{ color: orange[500] }} />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title='Edit'>
                        <IconButton
                          size='small'
                          onClick={() =>
                            'uuid' in combine
                              ? handleEdit(combine as SamlServiceProvider)
                              : handleFederationEdit(combine as FederationData)
                          }
                        >
                          <EditIcon style={{ color: orange[500] }} />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title='Remove'>
                        <IconButton
                          size='small'
                          onClick={() =>
                            'uuid' in combine
                              ? handleDeleteOpen(combine as SamlServiceProvider)
                              : handleFederationDeleteOpen(combine as FederationData)
                          }
                        >
                          <DeleteIcon style={{ color: red[500] }} />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {combinedData.length > 0 && (
        <TablePagination
          component='div'
          count={combinedData.length}
          page={page}
          onChangePage={(_: any, newPage: number) => setPage(newPage)}
          rowsPerPage={perPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onChangeRowsPerPage={(e: React.ChangeEvent<HTMLInputElement>) => {
            setPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          style={{ marginTop: 8 }}
        />
      )}

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth='sm'
        PaperProps={{
          style: {
            borderRadius: 10,
            overflow: 'hidden',
            maxHeight: '90vh',
          },
        }}
      >
        {/* Header */}
        <div
          style={{
            background: '#4f7df3',
            color: '#fff',
            padding: '10px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexShrink: 0,
          }}
        >
          <Typography
            style={{
              fontSize: 16,
              fontWeight: 600,
              color: '#fff',
            }}
          >
            Add an application directly
          </Typography>
        </div>

        <form
          onSubmit={
            selectedAppName === 'Microsoft 365' ? handleSubmitFederation(onSubmitFederation) : handleSubmit(onSubmit)
          }
          style={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            flex: 1,
          }}
        >
          <DialogContent
            style={{
              padding: '16px 18px 18px',
              maxHeight: '70vh',
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            {/* Heading */}
            <Typography
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: '#263238',
                marginBottom: 2,
              }}
            >
              Provide the application's SAML details
            </Typography>

            <Typography
              style={{
                fontSize: 12,
                color: '#7b8794',
                marginBottom: 14,
              }}
            >
              Choose the method your application provides.
            </Typography>

            {/* Method Tabs */}
            <Grid container spacing={1} style={{ marginBottom: 16 }}>
              <Grid item xs={4}>
                <Button
                  fullWidth
                  disableElevation
                  variant='outlined'
                  onClick={() => {
                    setManual(false);
                    setMeta(false);
                    setUrl(true);
                    setMetadataPreview(null);
                    setMetadataUrl('');
                  }}
                  style={{
                    height: 34,
                    textTransform: 'none',
                    fontSize: 12,
                    fontWeight: 600,
                    borderRadius: 6,
                    border: url ? '2px solid #5b82f1' : '1px solid #dfe5ef',
                    backgroundColor: url ? '#eef3ff' : '#fff',
                    color: url ? '#4f7df3' : '#4b5563',
                  }}
                >
                  Metadata URL
                </Button>
              </Grid>

              <Grid item xs={4}>
                <Button
                  fullWidth
                  disableElevation
                  variant='outlined'
                  onClick={() => {
                    setManual(false);
                    setMeta(true);
                    setUrl(false);
                    setMetadataPreview(null);
                    setMetadataUrl('');
                  }}
                  style={{
                    height: 34,
                    textTransform: 'none',
                    fontSize: 12,
                    fontWeight: 600,
                    borderRadius: 6,
                    border: meta ? '2px solid #5b82f1' : '1px solid #dfe5ef',
                    backgroundColor: meta ? '#eef3ff' : '#fff',
                    color: meta ? '#4f7df3' : '#4b5563',
                  }}
                >
                  Upload XML
                </Button>
              </Grid>

              <Grid item xs={4}>
                <Button
                  fullWidth
                  disableElevation
                  variant='outlined'
                  onClick={() => {
                    setManual(true);
                    setMeta(false);
                    setUrl(false);
                    setMetadataPreview(null);
                    setMetadataUrl('');
                  }}
                  style={{
                    height: 34,
                    textTransform: 'none',
                    fontSize: 12,
                    fontWeight: 600,
                    borderRadius: 6,
                    border: manual ? '2px solid #5b82f1' : '1px solid #dfe5ef',
                    backgroundColor: manual ? '#eef3ff' : '#fff',
                    color: manual ? '#4f7df3' : '#4b5563',
                  }}
                >
                  Enter details manually
                </Button>
              </Grid>
            </Grid>

            {/* ================= METADATA URL ================= */}
            {url && (
              <>
                <Typography
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: 6,
                  }}
                >
                  Application metadata URL
                </Typography>

                <Grid container spacing={1}>
                  <Grid item xs>
                    <TextField
                      fullWidth
                      variant='outlined'
                      placeholder='Paste the URL supplied by the application'
                      value={metadataUrl}
                      onChange={(e) => setMetadataUrl(e.target.value)}
                      size='small'
                      InputProps={{
                        style: {
                          height: 36,
                          fontSize: 12,
                          background: '#f8fafc',
                        },
                      }}
                    />
                  </Grid>

                  <Grid item>
                    <Button
                      variant='contained'
                      disableElevation
                      disabled={!metadataUrl || autofillMetadata.isLoading}
                      onClick={() => {
                        setMetadataPreview(null);

                        autofillMetadata.mutate({
                          url: metadataUrl,
                        });
                      }}
                      style={{
                        height: 36,
                        minWidth: 130,
                        textTransform: 'none',
                        fontSize: 12,
                        fontWeight: 600,
                        borderRadius: 5,
                        backgroundColor: '#4f7df3',
                        color: '#fff',
                      }}
                    >
                      {autofillMetadata.isLoading ? (
                        <CircularProgress size={18} style={{ color: '#fff' }} />
                      ) : (
                        'Fetch metadata'
                      )}
                    </Button>
                  </Grid>
                </Grid>

                {metadataPreview && (
                  <div
                    style={{
                      marginTop: 16,
                      padding: 14,
                      background: '#f8fafc',
                      border: '1px solid #dfe5ef',
                      borderRadius: 6,
                    }}
                  >
                    <Typography
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: '#263238',
                        marginBottom: 12,
                      }}
                    >
                      Review imported details
                    </Typography>

                    <Typography
                      style={{
                        fontSize: 11,
                        color: '#7b8794',
                        marginBottom: 14,
                        lineHeight: 1.5,
                      }}
                    >
                      Confirm the detected application details before adding the service provider.
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          label='Application Name'
                          fullWidth
                          size='small'
                          variant='outlined'
                          {...register('name')}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          label='Application Entity ID'
                          fullWidth
                          size='small'
                          variant='outlined'
                          {...register('entity_id')}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          label='Reply URL (ACS URL)'
                          fullWidth
                          size='small'
                          variant='outlined'
                          {...register('acs_url')}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          label='Name ID Format'
                          fullWidth
                          size='small'
                          variant='outlined'
                          {...register('name_id_format')}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          label='Name ID Attribute'
                          fullWidth
                          size='small'
                          variant='outlined'
                          {...register('name_id_attribute')}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </Grid>
                  </div>
                )}

                {!metadataPreview && (
                  <div
                    style={{
                      marginTop: 16,
                      padding: '10px 14px',
                      background: '#f1f5ff',
                      border: '1px solid #dbe5ff',
                      borderRadius: 6,
                    }}
                  >
                    <Typography
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: '#374151',
                        marginBottom: 3,
                      }}
                    >
                      Next: review imported details
                    </Typography>

                    <Typography
                      style={{
                        fontSize: 11,
                        lineHeight: 1.5,
                        color: '#7b8794',
                      }}
                    >
                      We will detect the application name, Entity ID, Reply URL and certificates from the metadata.
                    </Typography>
                  </div>
                )}
              </>
            )}

            {/* ================= UPLOAD XML ================= */}
            {meta && (
              <div>
                <Typography
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: 6,
                  }}
                >
                  Upload application metadata
                </Typography>

                <Typography
                  style={{
                    fontSize: 11,
                    color: '#7b8794',
                    marginBottom: 12,
                    lineHeight: 1.5,
                  }}
                >
                  Upload the XML metadata file provided by your application.
                </Typography>

                <input
                  type='file'
                  accept='.xml,.json'
                  style={{ display: 'none' }}
                  id='metadata-file-input'
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const file = e.target.files?.[0];

                    if (!file) return;

                    const fd = new FormData();
                    fd.append('metadata_file', file);

                    setMetadataPreview(null);
                    autofillMetadata.mutate(fd);
                  }}
                />

                <label
                  htmlFor='metadata-file-input'
                  style={{
                    display: 'block',
                    width: '100%',
                  }}
                >
                  <Button
                    variant='outlined'
                    component={'span' as any}
                    fullWidth
                    style={{
                      height: 40,
                      textTransform: 'none',
                      borderRadius: 6,
                    }}
                  >
                    {autofillMetadata.isLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      'Choose Metadata File (.xml / .json)'
                    )}
                  </Button>
                </label>

                {metadataPreview && (
                  <div
                    style={{
                      marginTop: 16,
                      padding: 14,
                      background: '#f8fafc',
                      border: '1px solid #dfe5ef',
                      borderRadius: 6,
                    }}
                  >
                    <Typography
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        marginBottom: 4,
                        color: '#263238',
                      }}
                    >
                      Review imported details
                    </Typography>

                    <Typography
                      style={{
                        fontSize: 11,
                        color: '#7b8794',
                        marginBottom: 14,
                      }}
                    >
                      Confirm the detected details before adding the application.
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          label='Application Name'
                          fullWidth
                          size='small'
                          variant='outlined'
                          {...register('name')}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          label='Application Entity ID'
                          fullWidth
                          size='small'
                          variant='outlined'
                          {...register('entity_id')}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          label='Reply URL (ACS URL)'
                          fullWidth
                          size='small'
                          variant='outlined'
                          {...register('acs_url')}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          label='Name ID Format'
                          fullWidth
                          size='small'
                          variant='outlined'
                          {...register('name_id_format')}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          label='Name ID Attribute'
                          fullWidth
                          size='small'
                          variant='outlined'
                          {...register('name_id_attribute')}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </Grid>
                  </div>
                )}
              </div>
            )}

            {/* ================= MANUAL ================= */}
            {manual && (
              <div>
                <Typography
                  style={{
                    fontSize: 11,
                    color: '#7b8794',
                    marginBottom: 14,
                    lineHeight: 1.5,
                  }}
                >
                  Enter the SAML application details such as the application name, Entity ID, and Reply URL. NameID
                  options can be configured below when required.
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Controller
                      name='name'
                      control={control}
                      rules={{ required: 'AppName is required' }}
                      render={({ field }) => (
                        <>
                          <TextField
                            select
                            label='App Name'
                            fullWidth
                            variant='outlined'
                            value={field.value || ''}
                            onChange={(e) => {
                              const value = e.target.value;

                              if (value === OTHER) {
                                field.onChange(OTHER);
                                return;
                              }

                              field.onChange(value);
                              handlePresetChange(value);
                              handleFederationPresetChange(value);
                            }}
                            error={!!errors.name}
                            helperText={errors.name?.message}
                          >
                            {serviceProviderPresets.map((p) => (
                              <MenuItem key={p.name} value={p.name}>
                                {p.name}
                              </MenuItem>
                            ))}

                            {/* <MenuItem value={OTHER}>Other</MenuItem> */}
                          </TextField>

                          {field.value === OTHER && (
                            <Controller
                              name='customAppName'
                              control={control}
                              rules={{
                                required: 'App Name is required',
                              }}
                              render={({ field: customField }) => (
                                <TextField
                                  {...customField}
                                  label='Enter App Name'
                                  fullWidth
                                  variant='outlined'
                                  margin='normal'
                                  error={!!errors.customAppName}
                                  helperText={errors.customAppName?.message}
                                />
                              )}
                            />
                          )}
                        </>
                      )}
                    />
                  </Grid>

                  {selectedAppName !== 'Microsoft 365' ? (
                    <>
                      <Grid item xs={12}>
                        <TextField
                          label='Application Entity ID'
                          fullWidth
                          variant='outlined'
                          {...register('entity_id', {
                            required: 'Application Entity ID is required',
                          })}
                          error={!!errors.entity_id}
                          helperText={
                            errors.entity_id?.message || 'The unique identifier provided by the SAML application.'
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          label='Reply URL (ACS URL)'
                          fullWidth
                          variant='outlined'
                          {...register('acs_url', {
                            required: 'Reply URL is required',
                          })}
                          error={!!errors.acs_url}
                          helperText={
                            errors.acs_url?.message || 'The URL where the application receives the SAML response.'
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <div
                          style={{
                            marginTop: 4,
                            padding: 12,
                            border: '1px solid #e5e7eb',
                            borderRadius: 6,
                            background: '#fafbfc',
                          }}
                        >
                          <Typography
                            style={{
                              fontSize: 12,
                              fontWeight: 700,
                              color: '#374151',
                              marginBottom: 3,
                            }}
                          >
                            Advanced SAML settings
                          </Typography>

                          <Typography
                            style={{
                              fontSize: 11,
                              color: '#7b8794',
                              marginBottom: 12,
                            }}
                          >
                            Configure NameID options only if the application requires a specific identifier format.
                          </Typography>

                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <TextField
                                label='Name ID Format'
                                fullWidth
                                variant='outlined'
                                {...register('name_id_format')}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <TextField
                                label='Name ID Attribute'
                                fullWidth
                                variant='outlined'
                                {...register('name_id_attribute')}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                          </Grid>
                        </div>
                      </Grid>
                    </>
                  ) : (
                    <>
                      <Grid item xs={12}>
                        <TextField
                          label='Tenant ID'
                          fullWidth
                          variant='outlined'
                          {...registerFederation('tenant_id', {
                            required: 'Tenant ID is required',
                          })}
                          error={!!federationErrors.tenant_id}
                          helperText={federationErrors.tenant_id?.message}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          label='Domain'
                          fullWidth
                          variant='outlined'
                          {...registerFederation('domain', {
                            required: 'Domain is required',
                          })}
                          error={!!federationErrors.domain}
                          helperText={federationErrors.domain?.message}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          label='Client ID'
                          fullWidth
                          variant='outlined'
                          {...registerFederation('azure_client_id', {
                            required: 'Client ID is required',
                          })}
                          error={!!federationErrors.azure_client_id}
                          helperText={federationErrors.azure_client_id?.message}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          label='Client Secret'
                          type='password'
                          fullWidth
                          variant='outlined'
                          {...registerFederation('azure_client_secret', {
                            required: 'Client Secret is required',
                          })}
                          error={!!federationErrors.azure_client_secret}
                          helperText={federationErrors.azure_client_secret?.message}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          label='Base URL'
                          fullWidth
                          variant='outlined'
                          {...registerFederation('base_url', {
                            required: 'Base URL is required',
                          })}
                          error={!!federationErrors.base_url}
                          helperText={federationErrors.base_url?.message}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>

                {/* Attribute Mapping */}
                {attrs.length > 0 && (
                  <div style={{ marginTop: 20 }}>
                    <Typography
                      variant='subtitle1'
                      style={{
                        fontWeight: 600,
                        marginBottom: 8,
                      }}
                    >
                      Attribute Mapping
                    </Typography>

                    <TableContainer component={Paper} variant='outlined'>
                      <Table size='small'>
                        <TableHead>
                          <TableRow>
                            <TableCell style={{ fontWeight: 600 }}>LDAP Attribute</TableCell>

                            <TableCell style={{ fontWeight: 600 }}>Source Attribute</TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {attrs.map((attr, i) => {
                            const destKey = attr.name;

                            const sourceOptions = attrs.map((a) => ({
                              label: a?.label,
                              key: a?.source,
                            }));

                            return (
                              <TableRow key={i}>
                                <TableCell>
                                  {attr.label}

                                  {attr.required && (
                                    <span
                                      style={{
                                        color: 'red',
                                        marginLeft: 4,
                                      }}
                                    >
                                      *
                                    </span>
                                  )}
                                </TableCell>

                                <TableCell>
                                  <FormControl fullWidth size='small'>
                                    <Select
                                      value={attrMappings?.[destKey] || ''}
                                      displayEmpty
                                      onChange={(event) => {
                                        setAttrMappings((prev) => ({
                                          ...prev,
                                          [destKey]: event.target.value as string,
                                        }));
                                      }}
                                    >
                                      <MenuItem value=''>None</MenuItem>

                                      {sourceOptions.map((opt) => (
                                        <MenuItem key={opt.key} value={opt.key}>
                                          {opt.label}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </div>
                )}

                {createSamlSP.isError && (
                  <Typography
                    style={{
                      color: 'red',
                      marginTop: 10,
                    }}
                  >
                    {(createSamlSP.error as any)?.response?.data?.message || 'Something went wrong'}
                  </Typography>
                )}
              </div>
            )}
          </DialogContent>

          <DialogActions
            style={{
              padding: '8px 18px 14px',
              flexShrink: 0,
              borderTop: '1px solid #e5e7eb',
              background: '#fff',
            }}
          >
            <Button
              onClick={handleClose}
              disabled={createSamlSP.isLoading}
              style={{
                textTransform: 'none',
                color: '#6b7280',
              }}
            >
              Cancel
            </Button>

            <Button
              type='submit'
              variant='contained'
              disabled={createSamlSP.isLoading || (!manual && !meta && !url) || ((meta || url) && !metadataPreview)}
              style={{
                textTransform: 'none',
                borderRadius: 5,
                backgroundColor: '#4f7df3',
                color: '#fff',
              }}
            >
              {createSamlSP.isLoading ? <CircularProgress size={20} style={{ color: '#fff' }} /> : 'Add application'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={viewOpen}
        onClose={handleViewClose}
        fullWidth
        maxWidth='xl'
        PaperProps={{
          style: {
            borderRadius: 12,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid #e5e7eb',
            background: '#fff',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
            <div>
              <Typography variant='h5' style={{ fontWeight: 700 }}>
                {selectedFederation?.name || selectedSaml?.name || 'SAML Service Provider'}
              </Typography>
              <Typography variant='body2' color='textSecondary' style={{ marginTop: 4 }}>
                {selectedFederation ? 'Federation Application Details' : 'SAML Service Provider Details'}
              </Typography>
            </div>

            {(selectedFederation || selectedSaml) && (
              <Chip
                label={selectedFederation?.is_active ?? selectedSaml?.is_active ? 'Active' : 'Inactive'}
                color='default'
                style={{
                  color: selectedFederation?.is_active ?? selectedSaml?.is_active ? '#4caf50' : '#666',
                  backgroundColor: selectedFederation?.is_active ?? selectedSaml?.is_active ? '#fff' : '#f5f5f5',
                  border: '1px solid black',
                  borderColor: selectedFederation?.is_active ?? selectedSaml?.is_active ? '#4caf50' : '#ccc',
                }}
              />
            )}
          </div>
        </DialogTitle>

        <DialogContent
          dividers
          style={{
            background: '#f5f7fb',
            padding: 24,
          }}
        >
          {selectedFederation ? (
            <Grid container spacing={3}>
              {/* LEFT - GUIDE */}
              <Grid item xs={12} md={5}>
                <Paper
                  elevation={0}
                  style={{
                    padding: 16,
                    height: '100%',
                    border: '1px solid #e5e7eb',
                    borderRadius: 12,
                    background: '#fff',
                  }}
                >
                  <Typography variant='h6' style={{ fontWeight: 700, marginBottom: 12 }}>
                    Workspace SAML Setup Guide
                  </Typography>

                  <div
                    onClick={handleImageZoomOpen}
                    style={{
                      width: '100%',
                      background: '#f8fafc',
                      borderRadius: 10,
                      padding: 10,
                      cursor: 'pointer',
                    }}
                  >
                    <img
                      src={getProviderImage(selectedFederation.name)}
                      alt='SAML Setup Guide'
                      style={{
                        width: '100%',
                        height: 'auto',
                        maxHeight: '70vh',
                        objectFit: 'contain',
                        display: 'block',
                      }}
                    />
                  </div>

                  <Typography variant='body2' color='textSecondary' style={{ marginTop: 12, lineHeight: 1.6 }}>
                    Follow these steps in the Workspace Admin Console to configure EasyIdentity as a SAML Identity
                    Provider.
                  </Typography>
                </Paper>
              </Grid>

              {/* RIGHT - FEDERATION DATA */}
              <Grid item xs={12} md={7}>
                <Paper
                  elevation={0}
                  style={{
                    padding: 20,
                    border: '1px solid #e5e7eb',
                    borderRadius: 12,
                    background: '#fff',
                  }}
                >
                  <Typography variant='h6' style={{ fontWeight: 700, marginBottom: 16 }}>
                    Federation Information
                  </Typography>

                  <Grid container spacing={2}>
                    {[
                      ['App Name', selectedFederation.name],
                      ['Tenant ID', selectedFederation.tenant_id],
                      ['Domain', selectedFederation.domain],
                      ['Status', selectedFederation.status],
                      ['Client ID', selectedFederation.azure_client_id],
                      ['Client Secret', selectedFederation.has_azure_client_secret ? 'Available' : 'Not Available'],
                      ['Federation ID', selectedFederation.federation_id],
                      ['Issuer URI', selectedFederation.issuer_uri],
                      ['SSO URL', selectedFederation.sso_url],
                      ['Logout URL', selectedFederation.logout_url],
                    ].map(([label, value]) => (
                      <Grid item xs={12} sm={6} key={label}>
                        <TextField
                          label={label}
                          fullWidth
                          variant='outlined'
                          value={value || 'Not Available'}
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                    ))}

                    <Grid item xs={12}>
                      <TextField
                        label='Signing Certificate'
                        fullWidth
                        multiline
                        rows={7}
                        variant='outlined'
                        value={selectedFederation.signing_certificate || 'Not Available'}
                        InputProps={{
                          readOnly: true,
                          style: { fontFamily: 'monospace', fontSize: 12 },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          ) : selectedSaml ? (
            <Grid container spacing={3}>
              {/* ================= LEFT ================= */}
              <Grid item xs={12} md={7}>
                {/* IDP DETAILS */}
                {selectedSaml.idp && (
                  <Paper
                    elevation={0}
                    style={{
                      padding: 20,
                      marginBottom: 20,
                      border: '1px solid #e5e7eb',
                      borderRadius: 12,
                      background: '#fff',
                    }}
                  >
                    <Typography variant='h6' style={{ fontWeight: 700, marginBottom: 16 }}>
                      EasyIdentity IdP Details
                    </Typography>

                    <Grid container spacing={2}>
                      {[
                        // ['Service Provider ID', selectedSaml.idp.service_provider_id],
                        ['Service Provider UUID', selectedSaml.idp.service_provider_uuid],
                        ['Service Provider Name', selectedSaml.idp.service_provider_name],
                        ['IdP Entity ID', selectedSaml.idp.entity_id],
                        ['IdP Metadata URL', selectedSaml.idp.metadata_url],
                        ['SSO URL', selectedSaml.idp.sso_url],
                        ['IdP Initiated SSO URL', selectedSaml.idp.idp_initiated_sso_url],
                        ['Signing Algorithm', selectedSaml.idp.signing_algorithm],
                        ['Digest Algorithm', selectedSaml.idp.digest_algorithm],
                      ].map(([label, value]) => {
                        const showCopy = [
                          'IdP Entity ID',
                          'IdP Metadata URL',
                          'SSO URL',
                          'IdP Initiated SSO URL',
                        ].includes(label as string);

                        return (
                          <Grid item xs={12} sm={6} key={label as string}>
                            <TextField
                              label={label as string}
                              fullWidth
                              variant='outlined'
                              value={value ?? 'Not Available'}
                              InputProps={{
                                readOnly: true,
                                endAdornment:
                                  showCopy && value ? (
                                    <Tooltip title='Copy'>
                                      <IconButton
                                        size='small'
                                        onClick={() => navigator.clipboard.writeText(String(value))}
                                      >
                                        <FileCopy fontSize='small' />
                                      </IconButton>
                                    </Tooltip>
                                  ) : undefined,
                              }}
                            />
                          </Grid>
                        );
                      })}
                      {/* {[
                        ['Service Provider ID', selectedSaml.idp.service_provider_id],
                        ['Service Provider UUID', selectedSaml.idp.service_provider_uuid],
                        ['Service Provider Name', selectedSaml.idp.service_provider_name],
                        ['IdP Entity ID', selectedSaml.idp.entity_id],
                        ['IdP Metadata URL', selectedSaml.idp.metadata_url],
                        ['SSO URL', selectedSaml.idp.sso_url],
                        ['IdP Initiated SSO URL', selectedSaml.idp.idp_initiated_sso_url],
                        ['Signing Algorithm', selectedSaml.idp.signing_algorithm],
                        ['Digest Algorithm', selectedSaml.idp.digest_algorithm]
                      ].map(([label, value]) => (
                        <Grid item xs={12} sm={6} key={label}>
                          <TextField
                            label={label}
                            fullWidth
                            variant='outlined'
                            value={value ?? 'Not Available'}
                            InputProps={{
                              readOnly: true,
                              endAdornment: value ? (
                                <Tooltip title='Copy'>
                                  <IconButton
                                    size='small'
                                    onClick={() => navigator.clipboard.writeText(String(value))}
                                  >
                                    <FileCopy fontSize='small' />
                                  </IconButton>
                                </Tooltip>
                              ) : undefined
                            }}
                          />
                        </Grid>
                      ))} */}
                    </Grid>
                  </Paper>
                )}
                {/* BASIC INFORMATION */}
                <Paper
                  elevation={0}
                  style={{
                    padding: 20,
                    marginBottom: 20,
                    border: '1px solid #e5e7eb',
                    borderRadius: 12,
                    background: '#fff',
                  }}
                >
                  <Typography variant='h6' style={{ fontWeight: 700, marginBottom: 16 }}>
                    Basic Information
                  </Typography>

                  <Grid container spacing={2}>
                    {[
                      // ['ID', selectedSaml.id],
                      ['UUID', selectedSaml.uuid],
                      ['Name', selectedSaml.name],
                      ['Description', selectedSaml.description],
                      ['Entity ID', selectedSaml.entity_id],
                      ['ACS URL', selectedSaml.acs_url],
                      ['Metadata URL', selectedSaml.metadata_url],
                      ['SLO URL', selectedSaml.slo_url],
                      ['Metadata Expires In', selectedSaml.metadata_expires_in],
                      ['Metadata Refresh Hour', selectedSaml.metadata_refresh_hour],
                    ].map(([label, value]) => (
                      <Grid item xs={12} sm={6} key={label}>
                        <TextField
                          label={label}
                          fullWidth
                          variant='outlined'
                          value={value ?? 'Not Available'}
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Paper>

                {/* SAML SETTINGS */}
                <Paper
                  elevation={0}
                  style={{
                    padding: 20,
                    marginBottom: 20,
                    border: '1px solid #e5e7eb',
                    borderRadius: 12,
                    background: '#fff',
                  }}
                >
                  <Typography variant='h6' style={{ fontWeight: 700, marginBottom: 16 }}>
                    Easyidentity SAML Settings
                  </Typography>

                  <Grid container spacing={2}>
                    {[
                      ['Name ID Format', selectedSaml.name_id_format],
                      ['Name ID Attribute', selectedSaml.name_id_attribute],
                      ['Sign Assertion', selectedSaml.sign_assertion ? 'Enabled' : 'Disabled'],
                      [
                        'Require Signed Authn Requests',
                        selectedSaml.require_signed_authn_requests ? 'Enabled' : 'Disabled',
                      ],
                      ['Allow IdP Initiated', selectedSaml.allow_idp_initiated ? 'Enabled' : 'Disabled'],
                      ['Active', selectedSaml.is_active ? 'Enabled' : 'Disabled'],
                      ['X509 Certificate', selectedSaml.has_x509_certificate ? 'Available' : 'Not Available'],
                    ].map(([label, value]) => (
                      <Grid item xs={12} sm={6} key={label}>
                        <TextField
                          label={label}
                          fullWidth
                          variant='outlined'
                          value={value ?? 'Not Available'}
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                    ))}

                    {selectedSaml.attribute_mapping && selectedSaml.attribute_mapping.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant='subtitle2' style={{ fontWeight: 600, marginBottom: 8 }}>
                          Attribute Mapping
                        </Typography>
                        <TableContainer component={Paper} variant='outlined'>
                          <Table size='small'>
                            <TableHead>
                              <TableRow>
                                <TableCell style={{ fontWeight: 600 }}>LDAP Attribute</TableCell>
                                <TableCell style={{ fontWeight: 600 }}>Source Attribute</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {selectedSaml.attribute_mapping.map((m, i) => (
                                <TableRow key={i}>
                                  <TableCell>{m.name}</TableCell>
                                  <TableCell>{m.source}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Grid>
                    )}

                    {selectedSaml.idp?.sso_bindings?.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant='subtitle2' style={{ marginBottom: 8 }}>
                          SSO Bindings
                        </Typography>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {selectedSaml.idp.sso_bindings.map((binding) => (
                            <Chip
                              key={binding}
                              label={
                                binding.includes('HTTP-POST')
                                  ? 'HTTP-POST'
                                  : binding.includes('HTTP-Redirect')
                                  ? 'HTTP-Redirect'
                                  : binding
                              }
                              variant='outlined'
                            />
                          ))}
                        </div>
                      </Grid>
                    )}
                  </Grid>
                </Paper>

                {/* CERTIFICATE DETAILS */}
                <Paper
                  elevation={0}
                  style={{
                    padding: 20,
                    marginBottom: 20,
                    border: '1px solid #e5e7eb',
                    borderRadius: 12,
                    background: '#fff',
                  }}
                >
                  <Typography variant='h6' style={{ fontWeight: 700, marginBottom: 16 }}>
                    Easyidentity Certificate Details
                  </Typography>

                  <Grid container spacing={2}>
                    {[
                      ['Certificate Status', selectedSaml.idp?.certificate_alert?.status],
                      ['Severity', selectedSaml.idp?.certificate_alert?.severity],
                      ['Days Remaining', selectedSaml.idp?.certificate_alert?.days_remaining],
                      // ['Threshold Days', selectedSaml.idp?.certificate_alert?.threshold_days],
                      [
                        'Valid From',
                        selectedSaml.idp?.certificate_alert?.certificate_valid_from
                          ? new Date(selectedSaml.idp.certificate_alert.certificate_valid_from).toLocaleString()
                          : null,
                      ],
                      [
                        'Valid Until',
                        selectedSaml.idp?.certificate_alert?.certificate_valid_until
                          ? new Date(selectedSaml.idp.certificate_alert.certificate_valid_until).toLocaleString()
                          : null,
                      ],
                      ['Message', selectedSaml.idp?.certificate_alert?.message],
                      ['Fingerprint SHA-256', selectedSaml.idp?.certificate_alert?.certificate_fingerprint_sha256],
                    ].map(([label, value]) => (
                      <Grid item xs={12} sm={6} key={label}>
                        <TextField
                          label={label}
                          fullWidth
                          variant='outlined'
                          value={value ?? 'Not Available'}
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                    ))}

                    {selectedSaml.idp?.certificate_alert?.thresholds?.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant='subtitle2' style={{ marginBottom: 8 }}>
                          Certificate Alert Thresholds
                        </Typography>

                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            flexWrap: 'nowrap',
                          }}
                        >
                          {selectedSaml.idp?.certificate_alert?.thresholds?.map((threshold) => (
                            <Chip key={threshold} label={`${threshold} days`} variant='outlined' />
                          ))}

                          <Button
                            variant='outlined'
                            color='primary'
                            startIcon={<GetApp />}
                            onClick={handleCertificateDownload}
                            disabled={!selectedSaml.certificate_download_url}
                            style={{ whiteSpace: 'nowrap' }}
                          >
                            Download Certificate
                          </Button>
                        </div>
                      </Grid>
                    )}

                    {/* <Grid item xs={12}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                       <TextField
                          label='Certificate Download URL'
                          fullWidth
                          multiline
                          rows={2}
                          variant='outlined'
                          value={selectedSaml.certificate_download_url || 'Not Available'}
                          InputProps={{
                            readOnly: true,
                            style: { fontFamily: 'monospace', fontSize: 12 },
                          }}
                        /> 

                        <IconButton
                          color='primary'
                          onClick={handleCertificateDownload}
                          disabled={!selectedSaml.certificate_download_url}
                          style={{
                            marginTop: 4,
                            border: '1px solid #ddd',
                            borderRadius: 6,
                          }}
                        >
                          <GetApp />
                        </IconButton>
                      </div>
                    </Grid> */}

                    <Grid item xs={12}>
                      <TextField
                        label='X509 Certificate'
                        fullWidth
                        multiline
                        rows={8}
                        variant='outlined'
                        value={selectedSaml.idp?.x509_certificate || 'Not Available'}
                        InputProps={{
                          readOnly: true,
                          style: {
                            fontFamily: 'monospace',
                            fontSize: 11,
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Paper>

                {/* SYSTEM INFORMATION */}
                <Paper
                  elevation={0}
                  style={{
                    padding: 20,
                    marginBottom: 20,
                    border: '1px solid #e5e7eb',
                    borderRadius: 12,
                    background: '#fff',
                  }}
                >
                  <Typography variant='h6' style={{ fontWeight: 700, marginBottom: 16 }}>
                    System Information
                  </Typography>

                  <Grid container spacing={2}>
                    {[
                      ['Last Used At', selectedSaml.last_used_at],
                      ['Created At', selectedSaml.created_at],
                      ['Updated At', selectedSaml.updated_at],
                    ].map(([label, value]) => (
                      <Grid item xs={12} sm={4} key={label}>
                        <TextField
                          label={label}
                          fullWidth
                          variant='outlined'
                          value={value ? new Date(value).toLocaleString() : 'Not Available'}
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Paper>

                {/* FIXED CONFIGURATION */}
                {selectedSaml.idp?.configuration_options?.fixed && (
                  <Paper
                    elevation={0}
                    style={{
                      padding: 20,
                      border: '1px solid #e5e7eb',
                      borderRadius: 12,
                      background: '#fff',
                    }}
                  >
                    <Typography variant='h6' style={{ fontWeight: 700, marginBottom: 16 }}>
                      Fixed Configuration
                    </Typography>

                    <Grid container spacing={2}>
                      {Object.entries(selectedSaml.idp.configuration_options.fixed).map(([key, value]) => (
                        <Grid item xs={12} sm={6} key={key}>
                          <TextField
                            label={key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())}
                            fullWidth
                            variant='outlined'
                            value={String(value ?? 'Not Available')}
                            InputProps={{ readOnly: true }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                )}
              </Grid>

              {/* ================= RIGHT ================= */}
              <Grid item xs={12} md={5}>
                {/* GUIDE */}
                <Paper
                  elevation={0}
                  style={{
                    padding: 16,
                    marginBottom: 20,
                    border: '1px solid #e5e7eb',
                    borderRadius: 12,
                    background: '#fff',
                  }}
                >
                  <Typography variant='h6' style={{ fontWeight: 700, marginBottom: 12 }}>
                    SAML Setup Guide
                  </Typography>

                  <div
                    onClick={handleImageZoomOpen}
                    style={{
                      width: '100%',
                      background: '#f8fafc',
                      borderRadius: 10,
                      padding: 10,
                      cursor: 'pointer',
                    }}
                  >
                    <img
                      src={getProviderImage(selectedSaml.name)}
                      alt={`${selectedSaml.name || 'SAML'} Setup Guide`}
                      style={{
                        width: '100%',
                        height: 'auto',
                        objectFit: 'contain',
                        display: 'block',
                      }}
                    />
                  </div>

                  <Typography variant='body2' color='textSecondary' style={{ marginTop: 12, lineHeight: 1.6 }}>
                    Follow these steps in the provider admin console to configure EasyIdentity as a SAML Identity
                    Provider.
                  </Typography>
                </Paper>

                {/* CONFIGURATION OPTIONS */}
                {selectedSaml.idp?.configuration_options && (
                  <Paper
                    elevation={0}
                    style={{
                      padding: 20,
                      marginBottom: 20,
                      border: '1px solid #e5e7eb',
                      borderRadius: 12,
                      background: '#fff',
                    }}
                  >
                    <Typography variant='h6' style={{ fontWeight: 700, marginBottom: 16 }}>
                      Easyidentity Configuration Options
                    </Typography>

                    {Object.entries(selectedSaml.idp.configuration_options)
                      .filter(([section]) => section !== 'fixed')
                      .map(([section, options]) => (
                        <div key={section} style={{ marginBottom: 20 }}>
                          <Typography
                            variant='subtitle1'
                            style={{ fontWeight: 600, marginBottom: 10, textTransform: 'capitalize' }}
                          >
                            {section.replace(/_/g, ' ')}
                          </Typography>

                          {options &&
                            Object.entries(options).map(([key, values]) => (
                              <div key={key} style={{ marginBottom: 14 }}>
                                {section === 'common' ? (
                                  <TextField
                                    select
                                    label={key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                                    fullWidth
                                    variant='outlined'
                                    size='small'
                                    defaultValue={Array.isArray(values) && values[0] ? String(values[0].value) : ''}
                                    InputProps={{ readOnly: true }}
                                  >
                                    {Array.isArray(values) &&
                                      values.map((option: any) => (
                                        <MenuItem key={String(option.value)} value={String(option.value)}>
                                          {option.label || String(option.value)}
                                        </MenuItem>
                                      ))}
                                  </TextField>
                                ) : (
                                  <>
                                    <Typography variant='body2' color='textSecondary' style={{ marginBottom: 6 }}>
                                      {key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())}
                                    </Typography>
                                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                      {Array.isArray(values) &&
                                        values.map((option: any) => (
                                          <Chip
                                            key={`${key}-${option.value}`}
                                            label={option.label || String(option.value)}
                                            size='small'
                                            variant='outlined'
                                          />
                                        ))}
                                    </div>
                                  </>
                                )}
                              </div>
                            ))}
                        </div>
                      ))}
                  </Paper>
                )}
              </Grid>
            </Grid>
          ) : (
            <Grid container justify='center' style={{ padding: 60 }}>
              <CircularProgress />
            </Grid>
          )}
        </DialogContent>

        <DialogActions
          style={{
            padding: '12px 24px',
            borderTop: '1px solid #e5e7eb',
            background: '#fff',
          }}
        >
          <Button onClick={handleViewClose} variant='contained' color='primary'>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={imageZoomOpen} onClose={handleImageZoomClose} fullScreen>
        <div
          style={{
            margin: '10px auto',
            backgroundColor: '#111',
          }}
        >
          {/* Close Button */}
          <IconButton
            onClick={handleImageZoomClose}
            style={{
              position: 'fixed',
              top: 15,
              right: 15,
              zIndex: 10,
              color: '#fff',
              backgroundColor: 'rgba(0,0,0,0.6)',
            }}
          >
            <Close />
          </IconButton>

          {/* Full Image */}
          <img
            src={getProviderImage(selectedFederation?.name || selectedSaml?.name)}
            alt={selectedFederation?.name || 'Provider Guide'}
            style={{
              // maxWidth: '95vw',
              // maxHeight: '95vh',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              display: 'block',
            }}
          />
        </div>
      </Dialog>
      <Dialog open={editOpen} onClose={handleEditClose} fullWidth maxWidth='sm'>
        <DialogTitle>Edit SAML Service Provider</DialogTitle>

        <form
          onSubmit={
            selectedEditName === 'Microsoft 365'
              ? handleSubmitFederationEdit(onFederationEditSubmit)
              : handleSubmitEdit(onEditSubmit)
          }
        >
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name='name'
                  control={controlEdit}
                  rules={{ required: 'App Name is required' }}
                  render={({ field }) => (
                    <>
                      <TextField
                        select
                        label='App Name'
                        fullWidth
                        variant='outlined'
                        value={field.value || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          field.onChange(value);
                          if (value !== OTHER) {
                            setValueEdit('customAppName', '');
                            handlePresetChange(value, true);
                          }
                        }}
                        error={!!errorsEdit.name}
                        helperText={errorsEdit.name?.message}
                      >
                        {serviceProviderPresets.map((p) => (
                          <MenuItem key={p.name} value={p.name}>
                            {p.name}
                          </MenuItem>
                        ))}
                        {/* <MenuItem value={OTHER}>Other</MenuItem> */}
                      </TextField>

                      {selectedEditName === OTHER && (
                        <Controller
                          name='customAppName'
                          control={controlEdit}
                          rules={{
                            required: 'App Name is required',
                          }}
                          render={({ field: customField, fieldState }) => (
                            <TextField
                              {...customField}
                              label='Enter App Name'
                              fullWidth
                              variant='outlined'
                              margin='normal'
                              error={!!fieldState.error}
                              helperText={fieldState.error?.message}
                            />
                          )}
                        />
                      )}
                    </>
                  )}
                />
              </Grid>

              {selectedEditName === 'Microsoft 365' ? (
                <>
                  <Grid item xs={12}>
                    <TextField
                      label='Tenant ID'
                      fullWidth
                      variant='outlined'
                      {...registerFederationEdit('tenant_id', { required: 'Tenant ID is required' })}
                      error={!!federationErrorsEdit.tenant_id}
                      helperText={federationErrorsEdit.tenant_id?.message}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label='Domain'
                      fullWidth
                      variant='outlined'
                      {...registerFederationEdit('domain', { required: 'Domain is required' })}
                      error={!!federationErrorsEdit.domain}
                      helperText={federationErrorsEdit.domain?.message}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label='Client ID'
                      fullWidth
                      variant='outlined'
                      {...registerFederationEdit('azure_client_id', { required: 'Client ID is required' })}
                      error={!!federationErrorsEdit.azure_client_id}
                      helperText={federationErrorsEdit.azure_client_id?.message}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label='Client Secret'
                      type='password'
                      fullWidth
                      variant='outlined'
                      {...registerFederationEdit('azure_client_secret')}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label='Base URL'
                      fullWidth
                      variant='outlined'
                      InputLabelProps={{ shrink: true }}
                      {...registerFederationEdit('base_url')}
                    />
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12}>
                    <TextField
                      label='Entity ID'
                      fullWidth
                      variant='outlined'
                      {...registerEdit('entity_id', { required: 'Entity ID is required' })}
                      error={!!errorsEdit.entity_id}
                      helperText={errorsEdit.entity_id?.message}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label='ACS URL'
                      fullWidth
                      variant='outlined'
                      {...registerEdit('acs_url', { required: 'ACS URL is required' })}
                      error={!!errorsEdit.acs_url}
                      helperText={errorsEdit.acs_url?.message}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label='Name ID Format'
                      fullWidth
                      variant='outlined'
                      {...registerEdit('name_id_format')}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label='Name ID Attribute'
                      fullWidth
                      variant='outlined'
                      {...registerEdit('name_id_attribute')}
                    />
                  </Grid>
                </>
              )}
            </Grid>

            {selectedEditName !== 'Microsoft 365' &&
              (() => {
                const editPreset = serviceProviderPresets.find((p) => p.name === selectedEditName);
                const editAttrs =
                  editPreset?.attributes ||
                  selectedSaml?.attribute_mapping?.map((m) => ({
                    name: m.name,
                    source: m.source,
                    label: m.name,
                    required: false,
                  })) ||
                  [];
                if (!editAttrs.length) return null;
                return (
                  <div style={{ marginTop: 16 }}>
                    <Typography variant='subtitle1' style={{ fontWeight: 600, marginBottom: 8 }}>
                      Attribute Mapping
                    </Typography>
                    <TableContainer component={Paper} variant='outlined' style={{ marginTop: 8 }}>
                      <Table size='small'>
                        <TableHead>
                          <TableRow>
                            <TableCell style={{ fontWeight: 600 }}>LDAP Attribute</TableCell>
                            <TableCell style={{ fontWeight: 600 }}>Source Attribute</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {editAttrs.map((attr, i) => {
                            const destKey = attr.name;
                            const sourceOptions = editAttrs.map((a) => ({
                              label: (a as any).label || a.name,
                              key: a.source,
                            }));
                            return (
                              <TableRow key={i}>
                                <TableCell>
                                  {(attr as any).label || attr.name}
                                  {(attr as any).required && <span style={{ color: 'red', marginLeft: 4 }}>*</span>}
                                </TableCell>
                                <TableCell>
                                  <FormControl fullWidth size='small'>
                                    <Select
                                      value={editAttrMappings?.[destKey] || ''}
                                      displayEmpty
                                      onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
                                        setEditAttrMappings((prev) => ({
                                          ...prev,
                                          [destKey]: event.target.value as string,
                                        }));
                                      }}
                                    >
                                      <MenuItem value=''>None</MenuItem>
                                      {sourceOptions.map((opt) => (
                                        <MenuItem key={opt.key} value={opt.key}>
                                          {opt.label}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </div>
                );
              })()}

            {(selectedEditName === 'Microsoft 365' ? updateFederation.isError : updateSamlSP.isError) && (
              <Typography style={{ color: 'red', marginTop: 10 }}>
                {((selectedEditName === 'Microsoft 365' ? updateFederation.error : updateSamlSP.error) as any)?.response
                  ?.data?.message || 'Something went wrong'}
              </Typography>
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={handleEditClose} disabled={updateSamlSP.isLoading || updateFederation.isLoading}>
              Cancel
            </Button>
            <Button
              type='submit'
              variant='contained'
              color='primary'
              disabled={updateSamlSP.isLoading || updateFederation.isLoading}
            >
              {updateSamlSP.isLoading || updateFederation.isLoading ? <CircularProgress size={20} /> : 'Update'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <Dialog open={deleteOpen} onClose={handleDeleteClose} maxWidth='xs' fullWidth>
        <DialogTitle>Delete Service Provider</DialogTitle>

        <DialogContent>
          <Typography>
            Are you sure you want to delete <b>{selectedSaml?.name}</b>?
          </Typography>

          {deleteSamlSP.isError && (
            <Typography
              style={{
                color: 'red',
                marginTop: 8,
              }}
            >
              {(deleteSamlSP.error as any)?.response?.data?.message || 'Something went wrong'}
            </Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleDeleteClose} disabled={deleteSamlSP.isLoading}>
            Cancel
          </Button>

          <Button
            variant='contained'
            style={{
              backgroundColor: red[500],
              color: '#fff',
            }}
            onClick={handleDeleteConfirm}
            disabled={deleteSamlSP.isLoading}
          >
            {deleteSamlSP.isLoading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={viewOpenFederation} onClose={handleFederationViewClose} fullWidth maxWidth='xl'>
        <DialogTitle>Federation Application Details</DialogTitle>
        <DialogContent>
          {selectedFederation && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={1}
                  style={{
                    padding: 16,
                    borderRadius: 8,
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant='h6' style={{ marginBottom: 5 }}>
                        Basic Details
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label='App Name'
                        fullWidth
                        variant='outlined'
                        value={selectedFederation.name || ''}
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label='Tenant ID'
                        fullWidth
                        variant='outlined'
                        value={selectedFederation.tenant_id || ''}
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label='Domain'
                        fullWidth
                        variant='outlined'
                        value={selectedFederation.domain || ''}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label='Status'
                        fullWidth
                        variant='outlined'
                        value={selectedFederation.status || ''}
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        variant='h6'
                        style={{
                          marginTop: 15,
                          marginBottom: 5,
                        }}
                      >
                        Details
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label=' Client ID'
                        fullWidth
                        variant='outlined'
                        value={selectedFederation.azure_client_id || ''}
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label=' Client Secret'
                        fullWidth
                        variant='outlined'
                        value={selectedFederation.has_azure_client_secret ? 'Available' : 'Not Available'}
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label='Federation ID'
                        fullWidth
                        variant='outlined'
                        value={selectedFederation.federation_id || ''}
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label='Issuer URI'
                        fullWidth
                        variant='outlined'
                        value={selectedFederation.issuer_uri || ''}
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography
                        variant='h6'
                        style={{
                          marginTop: 15,
                          marginBottom: 5,
                        }}
                      >
                        SSO Details
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label='SSO URL'
                        fullWidth
                        multiline
                        rows={2}
                        variant='outlined'
                        value={selectedFederation.sso_url || ''}
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label='Logout URL'
                        fullWidth
                        multiline
                        rows={2}
                        variant='outlined'
                        value={selectedFederation.logout_url || ''}
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                    </Grid>

                    {selectedFederation.attribute_mapping && selectedFederation.attribute_mapping.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant='subtitle2' style={{ fontWeight: 600, marginBottom: 8 }}>
                          Attribute Mapping
                        </Typography>
                        <TableContainer component={Paper} variant='outlined'>
                          <Table size='small'>
                            <TableHead>
                              <TableRow>
                                <TableCell style={{ fontWeight: 600 }}>LDAP Attribute</TableCell>
                                <TableCell style={{ fontWeight: 600 }}>Source Attribute</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {selectedFederation.attribute_mapping.map((m, i) => (
                                <TableRow key={i}>
                                  <TableCell>{m.name}</TableCell>
                                  <TableCell>{m.source}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Typography
                        variant='h6'
                        style={{
                          marginTop: 15,
                          marginBottom: 5,
                        }}
                      >
                        Signing Certificate
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label='Signing Certificate'
                        fullWidth
                        multiline
                        rows={6}
                        variant='outlined'
                        value={selectedFederation.signing_certificate || ''}
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={2}
                  style={{ padding: 12, height: '100%', backgroundColor: '#f7f9fc', borderRadius: 8 }}
                >
                  <Typography
                    variant='h6'
                    style={{
                      marginBottom: 12,
                      fontWeight: 600,
                    }}
                  >
                    Workspace SAML Setup Guide
                  </Typography>
                  <div
                    style={{
                      width: '100%',
                      textAlign: 'center',
                      backgroundColor: '#fff',
                      borderRadius: 6,
                      padding: 8,
                    }}
                  >
                    <div
                      onClick={handleImageZoomOpen}
                      style={{
                        width: '100%',
                        backgroundColor: '#fff',
                        borderRadius: 6,
                        padding: 8,
                        cursor: 'pointer',
                        textAlign: 'center',
                      }}
                    >
                      <img
                        src={microsoftguidlineImage}
                        alt='Workspace SAML Setup Guide'
                        style={{
                          width: '100%',
                          height: 'auto',
                          objectFit: 'contain',
                          display: 'block',
                          margin: '0 auto',
                        }}
                      />
                    </div>
                  </div>
                  <Typography variant='body2' color='textSecondary' style={{ marginTop: 12, lineHeight: 1.5 }}>
                    Follow these steps in Workspace Admin Console to configure EasyIdentity as a SAML Identity Provider.
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleFederationViewClose} variant='contained' color='primary'>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={editOpenFederation} onClose={handleFederationEditClose} fullWidth maxWidth='sm'>
        <DialogTitle>Edit Federation</DialogTitle>
        <form onSubmit={handleSubmitFederationEdit(onFederationEditSubmit)}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name='name'
                  control={controlFederationEdit}
                  rules={{ required: 'App Name is required' }}
                  render={({ field }) => (
                    <TextField
                      select
                      label='App Name'
                      fullWidth
                      variant='outlined'
                      value={field.value || ''}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                        handleFederationPresetChange(e.target.value, true);
                      }}
                      error={!!federationErrorsEdit.name}
                      helperText={federationErrorsEdit.name?.message}
                    >
                      {federationPresets.map((p) => (
                        <MenuItem key={p.name} value={p.name}>
                          {p.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label='Tenant ID'
                  fullWidth
                  variant='outlined'
                  {...registerFederationEdit('tenant_id', { required: 'Tenant ID is required' })}
                  error={!!federationErrorsEdit.tenant_id}
                  helperText={federationErrorsEdit.tenant_id?.message}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label='Domain'
                  fullWidth
                  variant='outlined'
                  {...registerFederationEdit('domain', { required: 'Domain is required' })}
                  error={!!federationErrorsEdit.domain}
                  helperText={federationErrorsEdit.domain?.message}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label=' Client ID'
                  fullWidth
                  variant='outlined'
                  {...registerFederationEdit('azure_client_id', { required: ' Client ID is required' })}
                  error={!!federationErrorsEdit.azure_client_id}
                  helperText={federationErrorsEdit.azure_client_id?.message}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label=' Client Secret'
                  type='password'
                  fullWidth
                  variant='outlined'
                  {...registerFederationEdit('azure_client_secret')}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField label='Base URL' fullWidth variant='outlined' {...registerFederationEdit('base_url')} />
              </Grid>
            </Grid>
            {selectedEditName === 'Microsoft 365' &&
              (() => {
                const editPreset = serviceProviderPresets.find((p) => p.name === selectedEditName);
                const editAttrs =
                  editPreset?.attributes ||
                  selectedFederation?.attribute_mapping?.map((m) => ({
                    name: m.name,
                    source: m.source,
                    label: m.name,
                    required: false,
                  })) ||
                  [];
                if (!editAttrs.length) return null;
                return (
                  <div style={{ marginTop: 16 }}>
                    <Typography variant='subtitle1' style={{ fontWeight: 600, marginBottom: 8 }}>
                      Attribute Mapping
                    </Typography>
                    <TableContainer component={Paper} variant='outlined' style={{ marginTop: 8 }}>
                      <Table size='small'>
                        <TableHead>
                          <TableRow>
                            <TableCell style={{ fontWeight: 600 }}>LDAP Attribute</TableCell>
                            <TableCell style={{ fontWeight: 600 }}>Source Attribute</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {editAttrs.map((attr, i) => {
                            const destKey = attr.name;
                            const sourceOptions = editAttrs.map((a) => ({
                              label: (a as any).label || a.name,
                              key: a.source,
                            }));
                            return (
                              <TableRow key={i}>
                                <TableCell>
                                  {(attr as any).label || attr.name}
                                  {(attr as any).required && <span style={{ color: 'red', marginLeft: 4 }}>*</span>}
                                </TableCell>
                                <TableCell>
                                  <FormControl fullWidth size='small'>
                                    <Select
                                      value={editAttrMappings?.[destKey] || ''}
                                      displayEmpty
                                      onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
                                        setEditAttrMappings((prev) => ({
                                          ...prev,
                                          [destKey]: event.target.value as string,
                                        }));
                                      }}
                                    >
                                      <MenuItem value=''>None</MenuItem>
                                      {sourceOptions.map((opt) => (
                                        <MenuItem key={opt.key} value={opt.key}>
                                          {opt.label}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </div>
                );
              })()}
            {updateFederation.isError && (
              <Typography style={{ color: 'red', marginTop: 10 }}>
                {(updateFederation.error as any)?.response?.data?.message || 'Something went wrong'}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleFederationEditClose} disabled={updateFederation.isLoading}>
              Cancel
            </Button>
            <Button type='submit' variant='contained' color='primary' disabled={updateFederation.isLoading}>
              {updateFederation.isLoading ? <CircularProgress size={20} /> : 'Update'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <Dialog open={deleteOpenFederation} onClose={handleFederationDeleteClose} maxWidth='xs' fullWidth>
        <DialogTitle>Delete Federation</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <b>{selectedFederation?.name}</b>?
          </Typography>
          {deleteFederation.isError && (
            <Typography style={{ color: 'red', marginTop: 8 }}>
              {(deleteFederation.error as any)?.response?.data?.message || 'Something went wrong'}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFederationDeleteClose} disabled={deleteFederation.isLoading}>
            Cancel
          </Button>
          <Button
            variant='contained'
            style={{ backgroundColor: red[500], color: '#fff' }}
            onClick={handleFederationDeleteConfirm}
            disabled={deleteFederation.isLoading}
          >
            {deleteFederation.isLoading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ServiceTable;
